import {Label} from "@client/components/Label";
import {ImperialCaloriesContext, useSiteContext} from "@client/site/context";
import * as shared from "@gesamtkunstwerk/shared/forms";
import {classNames, forms as reactivated} from "@reactivated";
import * as React from "react";

import * as widgets from "./widgets";

const styles = {
    form: "space-y-[15px]",
    fieldset: "space-y-[15px] flex flex-col",
    readOnly: "form-read-only",
} as const;

export {Label} from "@client/components/Label";

export type {FieldSchema} from "./fields";
export {
    DecimalInput,
    QuickSelect,
    QuickSelectField,
    SelectNumber,
    SelectText,
    Switch,
    Checkbox,
    TextInput,
} from "./widgets";

export class FormShell extends React.Component<{
    readOnly?: boolean;
    children: React.ReactNode;
}> {
    handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
        event.preventDefault();
    };
    render() {
        return (
            <form
                noValidate
                onSubmit={this.handleSubmit}
                className={classNames(
                    styles.form,
                    this.props.readOnly === true && styles.readOnly,
                )}
            >
                {this.props.children}
            </form>
        );
    }
}

export {useForm, useField, layout, FormErrors} from "@gesamtkunstwerk/shared/forms";

export const ready = shared.ready;

/**
 * For a hand-composed native POST form: the browser submits only when every
 * handle is ready, so an unreadable draft is shown beside its field instead of
 * posting a blank. `onReady` runs for a submit that goes through.
 */
export const guard =
    (handles: readonly shared.SubmittableForm<unknown>[], onReady?: () => void) =>
    (event: React.FormEvent<HTMLFormElement>) => {
        if (ready(...handles)) {
            onReady?.();
        } else {
            event.preventDefault();
        }
    };

/**
 * The shared helper, plus Joy's own policy for an error that names no field:
 * it is a toast, unless the screen shows it itself (`toasts: false`). The
 * library binds field errors and never reaches for a store.
 */
export async function submitForm<TValues, TResponse extends {readonly type: string}>(
    form: shared.SubmittableForm<TValues>,
    submit: (
        data: TValues,
    ) => Promise<TResponse & NoInfer<shared.BindableReply<TResponse>>>,
    options?: shared.SubmitOptions & {readonly toasts?: false},
): Promise<TResponse | null> {
    const response = await shared.submitForm(form, submit, options);

    if (response !== null && response.type === "invalid" && options?.toasts !== false) {
        const errors =
            (response as {errors?: readonly (shared.ReplyError | string)[]}).errors ??
            [];
        // A bare string is what the server sends for an assertion: it names no
        // field, exactly like a structured error with an empty `loc`.
        const modelErrors = errors
            .filter((error) => typeof error === "string" || error.loc.length === 0)
            .map((error) => (typeof error === "string" ? error : error.msg));

        if (modelErrors.length > 0) {
            const [{store}, {notify}] = await Promise.all([
                import("@client/store"),
                import("@client/features/notifications"),
            ]);
            for (const message of modelErrors) {
                store.dispatch(notify(message, {type: "error"}));
            }
        }
    }

    return response;
}
export type {
    FormDefinition,
    FieldDefinition,
    FieldKind,
    FormHandle,
    FieldHandle,
    EnumWidgetProps,
    SubmittableForm,
    ValueFields,
} from "@gesamtkunstwerk/shared/forms";
/**
 * What a bound form is holding for one text field, else its typed initial.
 *
 * A bound form's raw text is `drafts`, which survives an invalid POST; the
 * typed initial is `defaults` and is independent of any POST. A blank of either
 * kind answers null, so a caller's own `??` fallback still runs — the two
 * differ, and a `""` that reached a `??` chain would silently kill the rest.
 */
export function boundText(
    draft: string | boolean | null | undefined,
    initial: string | null | undefined,
): string | null {
    const held = typeof draft === "string" ? draft : draft == null ? initial : null;
    return held == null || held === "" ? null : held;
}

/**
 * The member of `options` equal to `value`, plus the raw text when there is
 * none.
 *
 * A choices column is checked by a form, never by the database, so a row may
 * hold a string that is no longer a member. That value is not silently turned
 * into a member: it rides back as `draft`, which keeps the field unready until
 * someone chooses. An absent value takes `missing`.
 */
export function asOption<
    TValue extends shared.AnyValue,
    // A blank the caller falls back to is a member, or the field's own null.
    // It never decides the domain, which the options state.
    TOutcome extends TValue | null,
>(
    options: readonly (readonly [TValue, string])[],
    value: unknown,
    outcome: {readonly missing: TOutcome; readonly unselected: TOutcome},
): {readonly value: TValue | TOutcome; readonly draft: string | undefined} {
    if (value == null) {
        return {value: outcome.missing, draft: undefined};
    }
    const member = options.find(([option]) => option === value);
    if (member != null) {
        return {value: member[0], draft: undefined};
    }
    // A stored choice is a column scalar. Anything else is a caller handing the
    // wrong prop, and a draft reading "[object Object]" would hide it.
    if (
        typeof value !== "string" &&
        typeof value !== "number" &&
        typeof value !== "boolean"
    ) {
        throw new TypeError(`asOption: a stored choice cannot be a ${typeof value}`);
    }
    return {value: outcome.unselected, draft: String(value)};
}

export const CSRFToken = reactivated.CSRFToken;

export const useIsImperial = () => {
    const {profile} = useSiteContext();
    const liveIsImperial = React.useContext(ImperialCaloriesContext).isImperial;

    return liveIsImperial ?? (profile == null ? true : profile.is_imperial);
};

export const useUseKilojoules = () => {
    const {profile} = useSiteContext();
    return profile == null ? false : profile.use_kilojoules;
};

export const useHasHiddenMacros = () => {
    const {profile} = useSiteContext();
    return profile == null ? false : profile.has_hidden_macros;
};

export function getFieldClass({readOnly}: {readOnly?: boolean}) {
    return classNames(
        "appearance-none text-sm block w-full min-w-0 flex-1 rounded-none rounded-l-md border-0 py-1.5 px-2 sm:px-3 text-gray-900 ring-1 ring-inset ring-stone-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600",
        readOnly == true && "bg-slate-100",
        readOnly != true && "bg-white",
    );
}

export function getSuffixClass({readOnly}: {readOnly?: boolean}) {
    return classNames(
        "text-[11px] sm:text-xs inline-flex items-center rounded-r-md border border-l-0 border-gray-300 px-[0.3rem] sm:px-2 text-gray-900 lowercase",
        readOnly == true && "bg-slate-100",
        // So dragging highlight doesn't show through.
        readOnly != true && "bg-white",
    );
}

export function handleInputFocusSelectAndPreventContextMenu(event: React.FocusEvent) {
    const target = event.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        // Chrome in responsive mode shows the context menu on focus.
        target.addEventListener(
            "contextmenu",
            (contextMenuEvent) => {
                contextMenuEvent.preventDefault();
            },
            true,
        );
        target.select();
    }
}

type Props<T extends shared.FieldKind> = {
    field: shared.FieldHandle<T>;
    className?: string;
};

function Weight({field, className}: Props<"weight">) {
    const isImperial = useIsImperial();
    return (
        <widgets.WeightInput {...field} isImperial={isImperial} className={className} />
    );
}
function Length({field}: Props<"length">) {
    const isImperial = useIsImperial();
    return <widgets.LengthInput {...field} isImperial={isImperial} />;
}
function Energy({field}: Props<"energy">) {
    const useKilojoules = useUseKilojoules();
    return <widgets.EnergyInput {...field} useKilojoules={useKilojoules} />;
}
function Multiplier({field}: Props<"multiplier">) {
    const isImperial = useIsImperial();
    return <widgets.WeightMultiplierInput {...field} isImperial={isImperial} />;
}

export const {Widget, createRenderer} = shared.createRenderers({
    widgets: {
        text: ({field, className}) => (
            <widgets.TextInput {...field} className={className} />
        ),
        email: ({field, className}) => (
            <widgets.EmailInput {...field} className={className} />
        ),
        password: ({field, className}) => (
            <widgets.PasswordInput {...field} className={className} />
        ),
        textarea: ({field, className}) => (
            <widgets.TextArea {...field} className={className} />
        ),
        markdown: ({field, className}) => (
            <widgets.MarkdownArea {...field} className={className} />
        ),
        decimal: ({field, className}) => (
            <widgets.DecimalInput {...field} className={className} />
        ),
        number: ({field, className}) => (
            <widgets.NumberInput {...field} integerOnly className={className} />
        ),
        boolean: ({field}) =>
            field.widget === "switch" ? (
                <widgets.Switch {...field} />
            ) : (
                <widgets.Checkbox {...field} />
            ),
        date: ({field}) =>
            field.widget === "select-date" ? (
                <shared.Widget field={field} className={widgets.widgetStyles.select} />
            ) : (
                <widgets.DateInput {...field} value={field.value ?? ""} />
            ),
        duration: ({field}) => (
            <widgets.DurationInput
                {...field}
                className={getFieldClass({readOnly: field.readOnly})}
                suffixClassName={getSuffixClass({readOnly: field.readOnly})}
            />
        ),
        // A short choice without a stated widget is the segmented control, as it
        // always was in Joy; "select" asks for the native dropdown.
        select: ({
            field,
            readOnly: _readOnly,
            placeholder: _placeholder,
            ...control
        }) =>
            field.widget === null || field.widget === "quick" ? (
                <widgets.QuickSelectField field={field} {...control} />
            ) : (
                <widgets.SelectField field={field} {...control} />
            ),
        weight: Weight,
        length: Length,
        energy: Energy,
        multiplier: Multiplier,
        percent: ({field}) => <widgets.PercentInput {...field} />,
        range: ({field}) => <widgets.RangeInput {...field} />,
    },
});

// Headless UI overwrites these ARIA props. Its `as` component applies our
// associations after that merge, preserving refs and event handlers.
type AssociatedProps<TElement extends "div" | "span"> =
    React.ComponentPropsWithoutRef<TElement> & {
        labelledBy?: string;
        describedBy?: string;
    };

export const AssociatedGroup = React.forwardRef<HTMLDivElement, AssociatedProps<"div">>(
    function AssociatedGroup({labelledBy, describedBy, ...rest}, ref) {
        return (
            <div
                {...rest}
                ref={ref}
                aria-labelledby={labelledBy ?? rest["aria-labelledby"]}
                aria-describedby={describedBy ?? rest["aria-describedby"]}
            />
        );
    },
);

export const AssociatedOption = React.forwardRef<
    HTMLSpanElement,
    AssociatedProps<"span">
>(function AssociatedOption({labelledBy, describedBy, ...rest}, ref) {
    return (
        <span
            {...rest}
            ref={ref}
            aria-labelledby={labelledBy ?? rest["aria-labelledby"]}
            aria-describedby={describedBy ?? rest["aria-describedby"]}
        />
    );
});

// Group associations belong to the rendered instance, not the field name.
// Every one is optional: the plain layout mints no ids and passes none.
export type Associations = {
    id?: string;
    "aria-labelledby"?: string;
    "aria-label"?: string;
    "aria-describedby"?: string;
    "aria-invalid"?: true;
};

function GroupField({
    field,
    label,
    help,
    className,
    renderWidget,
}: {
    field: Pick<shared.FieldHandle<shared.FieldKind>, "label" | "errors">;
    label: React.ReactNode | null;
    help: React.ReactNode | null;
    className?: string;
    renderWidget: (associations: Associations) => React.ReactNode;
}) {
    const generated = React.useId();
    const labelId = `${generated}-label`;
    const helpId = `${generated}-help`;
    const errorId = `${generated}-error`;
    const error = field.errors?.[0] ?? null;
    const describedBy =
        [help != null ? helpId : null, error != null ? errorId : null]
            .filter((id) => id != null)
            .join(" ") || undefined;

    return (
        <div className={className}>
            {label != null && <div id={labelId}>{label}</div>}
            {help != null && <div id={helpId}>{help}</div>}
            {renderWidget({
                id: `${generated}-control`,
                "aria-labelledby": label != null ? labelId : undefined,
                "aria-label": label == null ? field.label : undefined,
                "aria-describedby": describedBy,
                "aria-invalid": error != null ? true : undefined,
            })}
            {error != null && (
                <div
                    id={errorId}
                    role="alert"
                    className={widgets.widgetStyles.FieldError}
                >
                    {error}
                </div>
            )}
        </div>
    );
}

export const Fields = createRenderer(
    (
        field,
        props: {
            noLabel?: boolean;
            label?: React.ReactNode | false;
            help?: React.ReactNode | false;
            suffix?: string;
            className?: string;
            group?: boolean;
        },
        renderWidget: (associations: Associations) => React.ReactNode,
    ) =>
        props.group === true ? (
            <GroupField
                field={field}
                label={
                    props.noLabel === true || props.label === false
                        ? null
                        : "label" in props
                          ? props.label
                          : field.label
                }
                help={props.help === false ? null : (props.help ?? field.helpText)}
                className={props.className}
                renderWidget={renderWidget}
            />
        ) : (
            <Label
                // `false` suppresses the label the same way here as in the
                // group layout; passing it through renders an empty shell.
                label={
                    props.noLabel === true || props.label === false
                        ? null
                        : "label" in props
                          ? props.label
                          : field.label
                }
                error={field.errors?.[0] ?? null}
            >
                {/* Through renderWidget, not Widget: the slot is already bound
                    to the component and the props this callsite named. */}
                {props.suffix != null ? (
                    <widgets.Suffixed suffix={props.suffix}>
                        {renderWidget({})}
                    </widgets.Suffixed>
                ) : (
                    renderWidget({})
                )}
                {field.helpText != null && (
                    <p className="text-sm text-gray-500">{field.helpText}</p>
                )}
            </Label>
        ),
);

export const Field = Fields.Field;
export const Form = Fields.Form;
